import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Square } from "lucide-react";

const Terminal = ({ isOpen, onClose }) => {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef();
  const terminalRef = useRef();

  // ASCII Art for welcome
  const asciiArt = `
 ███╗   ██╗████████╗██╗     
 ████╗  ██║╚══██╔══╝██║     
 ██╔██╗ ██║   ██║   ██║     
 ██║╚██╗██║   ██║   ██║     
 ██║ ╚████║   ██║   ███████╗
 ╚═╝  ╚═══╝   ╚═╝   ╚══════╝
                             
 Welcome to Portfolio Terminal v2.0.1
 Type 'help' for available commands
`;

  const commands = {
    help: () => {
      const helpText = `
Available Commands:
═══════════════════

help        Show this help message
about       Display information about me  
whoami      Display current user info
education   Show educational background
skills      List technical skills
experience  Show work experience
contact     Display contact information
projects    Show recent projects
download    Download resume file
ls          List available files
cat <file>  Display file contents
clear       Clear terminal screen
date        Show current date
pwd         Show current directory  
uname       System information
history     Show command history
exit        Close terminal
`;
      return helpText;
    },

    about: () => `
About Nguyen Thanh Long
═══════════════════════════

🧑‍💻 Senior Software Engineer & Team Lead
📍 Ho Chi Minh City, Vietnam
💼 Currently working at Ringkas
🎓 Bach Khoa University - Computer Science
🤖 AI Advisory & Document Processing Specialist
⏱️  6+ years of experience

I am a dedicated software engineer with expertise in modern web
technologies and AI/ML solutions. I lead cross-functional teams and
specialize in building scalable applications using React, Node.js,
cloud platforms, and AI technologies like LangChain and LlamaIndex.
Passionate about AI advisory and document processing solutions.
`,

    whoami: () => `
longnt
Full Name: Nguyen Thanh Long
Role: Senior Software Engineer & Team Lead
Education: Bach Khoa University - Computer Science
Status: Available for opportunities
`,

    education: () => `
Educational Background
═════════════════════

🎓 Bach Khoa University
   (Ho Chi Minh City University of Technology)
   Major: Computer Science
   Location: Ho Chi Minh City, Vietnam

📚 Key Areas of Study:
   • Software Engineering Principles
   • Data Structures & Algorithms
   • Database Management Systems
   • Computer Networks & Security
   • Object-Oriented Programming

💡 Self-Directed Learning:
   • Modern Web Development Frameworks
   • Cloud Computing & DevOps
   • Agile Development Methodologies
   • AI/ML Technologies (LangChain, LlamaIndex)
`,

    skills: () => {
      const skillCategories = {
        Frontend: ["JavaScript", "React.js", "Vue.js", "HTML/CSS"],
        Backend: ["Node.js", "Go", "PHP", "Laravel"],
        "Database & Cloud": ["PostgreSQL", "MongoDB", "Redis", "AWS Lambda"],
        "AI & ML": ["LangChain", "LangSmith", "LlamaIndex", "LlamaParse"],
        "Tools & DevOps": ["Docker", "GraphQL", "CI/CD", "Git"],
      };

      let output = `
Technical Skills
══════════════════
`;

      Object.entries(skillCategories).forEach(([category, skills]) => {
        output += `\n${category}:\n`;
        skills.forEach((skill) => {
          output += `  ✓ ${skill}\n`;
        });
      });

      return output;
    },

    experience: () => {
      const experiences = [
        {
          title: "Senior Software Engineer & Team Lead",
          company: "Ringkas",
          period: "December 2023 - Present",
          location: "Ho Chi Minh City",
          highlights: [
            "Led cross-functional team of 6 engineers",
            "Improved data handling capacity by 500%",
            "Implemented AI document processing systems with LangChain & LlamaIndex",
            "Built AI advisory platform using LangSmith",
            "Technologies: Node.js, React.js, PostgreSQL, GraphQL, Lambda, AI/ML",
          ],
        },
        {
          title: "Software Engineer",
          company: "NFQ Asia",
          period: "April 2022 - December 2023",
          location: "Ho Chi Minh City",
          highlights: [
            "Developed multiple web applications",
            "Achieved 25% reduction in development time",
            "Improved user experience by 37%",
            "Built HR Management System with Node.js & React.js",
          ],
        },
        {
          title: "Software Engineer",
          company: "Adtima-Zalo-VNG",
          period: "October 2020 - February 2022",
          location: "Ho Chi Minh City",
          highlights: [
            "Developed campaign management systems",
            "Built web applications for ad management",
            "Utilized Go, Node.js, Redis, MongoDB, Docker",
          ],
        },
      ];

      let output = `
Professional Experience
══════════════════════════
`;

      experiences.forEach((exp, index) => {
        output += `\n${index + 1}. ${exp.title}\n`;
        output += `   Company: ${exp.company}\n`;
        output += `   Period: ${exp.period}\n`;
        output += `   Location: ${exp.location}\n`;
        output += `   Key Achievements:\n`;
        exp.highlights.forEach((highlight) => {
          output += `   • ${highlight}\n`;
        });
      });

      return output;
    },

    contact: () => `
Contact Information
═══════════════════

📧 Email: thanhlong6897@gmail.com
📱 Phone: +84 784 811 073
📍 Location: Ho Chi Minh City, Vietnam
💼 LinkedIn: https://www.linkedin.com/in/thanh-long-nguyen-318ba824b

Feel free to reach out for collaboration opportunities!
`,

    projects: () => `
Recent Projects
═══════════════

🚀 Fintech Processing Pipeline
   High-throughput data processing system
   Tech: Node.js, React.js, PostgreSQL, AWS Lambda

🤖 AI Document Processing System
   Intelligent document analysis and processing
   Tech: LangChain, LlamaIndex, LlamaParse, Python

🧠 AI Advisory Platform
   AI-powered advisory and consultation system
   Tech: LangSmith, LangChain, React.js, Node.js

👥 Human Resources Management System
   Complete HR solution for enterprise clients
   Tech: Node.js, React.js, Redis, MySQL

📱 Campaign Management Platform
   Advertising campaign management and analytics
   Tech: Go, Node.js, MongoDB, Docker
`,

    download: () => {
      // Download the existing PDF resume
      const link = document.createElement("a");
      link.href = "/nguyen-thanh-long-resume.pdf";
      link.download = "Nguyen_Thanh_Long_Resume.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return `\nDownloading resume...\n✅ Resume downloaded successfully!\n`;
    },

    ls: () => {
      const files = [
        "resume.txt",
        "skills.json",
        "experience.log",
        "contact.vcf",
        "projects.md",
        "ai_projects.md",
        "certifications.pdf",
        "portfolio.html",
      ];

      return `
${files.join("  ")}

💡 Tip: Type 'cat <filename>' to view file contents
💡 Tip: Type 'download' to get a formatted resume file
`;
    },

    cat: (args) => {
      if (!args.length) {
        return "Usage: cat <filename>";
      }

      const filename = args[0];
      const files = {
        "resume.txt": () => `
NGUYEN THANH LONG - SENIOR SOFTWARE ENGINEER
════════════════════════════════════════════════

Email: thanhlong6897@gmail.com
Phone: +84 784 811 073
Location: Ho Chi Minh City, Vietnam

EDUCATION:
Bach Khoa University - Computer Science
Ho Chi Minh City University of Technology

EXPERIENCE: 6+ years in software development
CURRENT ROLE: Senior Software Engineer & Team Lead at Ringkas
SPECIALTIES: React.js, Node.js, Team Leadership, AI/ML
AI FOCUS: Document Processing, Advisory Systems
`,

        "skills.json": () => {
          const skills = {
            frontend: ["JavaScript", "React.js", "Vue.js", "HTML/CSS"],
            backend: ["Node.js", "Go", "PHP", "Laravel"],
            database: ["PostgreSQL", "MongoDB", "Redis"],
            cloud: ["AWS Lambda", "Docker", "CI/CD"],
            ai_ml: ["LangChain", "LangSmith", "LlamaIndex", "LlamaParse"],
            tools: ["Git", "GraphQL"],
          };
          return JSON.stringify(skills, null, 2);
        },

        "ai_projects.md": () => `
# AI & Document Processing Projects

## Document Processing Pipeline
- **Technology Stack**: LangChain, LlamaIndex, LlamaParse
- **Purpose**: Intelligent document analysis and extraction
- **Features**: PDF parsing, content extraction, semantic search

## AI Advisory System
- **Technology Stack**: LangSmith, LangChain, GPT models
- **Purpose**: Automated advisory and consultation platform
- **Features**: Natural language processing, decision trees

## Key Accomplishments
✓ Improved document processing speed by 300%
✓ Built AI advisory system serving 1000+ users
✓ Integrated multiple LLM providers for redundancy
✓ Implemented semantic search with vector databases
`,

        "contact.vcf": () => `
BEGIN:VCARD
VERSION:3.0
FN:Nguyen Thanh Long
ORG:Ringkas
TITLE:Senior Software Engineer & Team Lead
EMAIL:thanhlong6897@gmail.com
TEL:+84784811073
END:VCARD
`,

        "experience.log": () => `
[2023-12-01] Started as Senior Software Engineer & Team Lead at Ringkas
[2023-12-15] Led first cross-functional team meeting
[2024-01-01] Implemented AI document processing pipeline
[2024-02-01] Achieved 500% improvement in data handling capacity
[2024-03-01] Launched AI advisory platform using LangSmith
[2024-04-01] Team expanded to 6 engineers under my leadership
`,

        "projects.md": () => `
# Recent Projects Portfolio

## 🚀 Fintech Processing Pipeline
**Duration**: 6 months  
**Team Size**: 6 engineers  
**Impact**: 500% performance improvement  

## 🤖 AI Document Processing
**Technologies**: LangChain, LlamaIndex, LlamaParse  
**Status**: Production  
**Users**: 1000+ active  

## 🧠 AI Advisory Platform
**Framework**: LangSmith integration  
**Features**: Natural language processing  
**Deployment**: Cloud-native architecture  
`,

        "certifications.pdf": () => `
CERTIFICATIONS & ACHIEVEMENTS
═══════════════════════════════

🏆 AWS Certified Solutions Architect
🏆 React.js Professional Certification
🏆 Node.js Advanced Developer
🏆 Agile Project Management
🏆 Team Leadership Excellence
🏆 AI/ML Specialization (LangChain)

Note: This is a text representation of the PDF file.
Actual certificates are available upon request.
`,
      };

      if (files[filename]) {
        return files[filename]();
      } else {
        return `cat: ${filename}: No such file or directory`;
      }
    },

    pwd: () => "/home/longnt/portfolio",

    date: () => new Date().toString(),

    uname: () => `
Portfolio-OS 1.0.0 (Terminal Mode)
Built with: React.js, JavaScript, CSS3, HTML5
Author: Nguyen Thanh Long
Architecture: x64
Kernel: Portfolio-Terminal-v2.0.1
`,

    history: () => {
      let output = "\nCommand History:\n";
      commandHistory.slice(-10).forEach((cmd, index) => {
        output += `${commandHistory.length - 10 + index + 1}: ${cmd}\n`;
      });
      return output;
    },

    clear: () => {
      setHistory([]);
      return "";
    },

    exit: () => {
      setTimeout(() => {
        onClose();
      }, 1000);
      return "Goodbye! 👋";
    },
  };

  const executeCommand = (cmd) => {
    const trimmedCmd = cmd.trim();
    if (trimmedCmd === "") return;

    const args = trimmedCmd.split(" ");
    const command = args[0].toLowerCase();
    const commandArgs = args.slice(1);

    const newHistory = [...history, { type: "command", content: trimmedCmd }];

    if (commands[command]) {
      const result = commands[command](commandArgs);
      if (result) {
        newHistory.push({ type: "output", content: result });
      }
    } else {
      newHistory.push({
        type: "error",
        content: `Command not found: ${command}. Type 'help' for available commands.`,
      });
    }

    setHistory(newHistory);
    setCommandHistory([...commandHistory, trimmedCmd]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      executeCommand(input);
      setInput("");
      setHistoryIndex(-1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex =
          historyIndex + 1 < commandHistory.length
            ? historyIndex + 1
            : commandHistory.length - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || "");
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || "");
      } else {
        setHistoryIndex(-1);
        setInput("");
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      // Tab completion
      const availableCommands = Object.keys(commands);
      const matches = availableCommands.filter((cmd) =>
        cmd.startsWith(input.toLowerCase())
      );
      if (matches.length === 1) {
        setInput(matches[0]);
      } else if (matches.length > 1) {
        const newHistory = [
          ...history,
          { type: "info", content: matches.join("  ") },
        ];
        setHistory(newHistory);
      }
    }
  };

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();

      // Add welcome message and ASCII art on first open
      if (history.length === 0) {
        setHistory([
          { type: "ascii", content: asciiArt },
          {
            type: "info",
            content:
              'Type "help" to see available commands or "about" to learn more about me.',
          },
        ]);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="terminal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="terminal-window"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="terminal-header">
            <div className="terminal-controls">
              <button className="control-btn close" onClick={onClose}>
                <X size={10} />
              </button>
              <button className="control-btn minimize">
                <Minus size={10} />
              </button>
              <button className="control-btn maximize">
                <Square size={10} />
              </button>
            </div>
            <div className="terminal-title">longnt@portfolio:~$</div>
          </div>

          <div className="terminal-body" ref={terminalRef}>
            {history.map((entry, index) => (
              <div key={index} className={`terminal-line ${entry.type}`}>
                {entry.type === "command" && (
                  <span className="prompt">longnt@portfolio:~$ </span>
                )}
                {entry.type === "ascii" ? (
                  <pre
                    style={{
                      color: "#00ff41",
                      fontFamily: "monospace",
                      fontSize: "12px",
                      lineHeight: "1.2",
                    }}
                  >
                    {entry.content}
                  </pre>
                ) : (
                  <pre
                    style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                  >
                    {entry.content}
                  </pre>
                )}
              </div>
            ))}

            <div className="terminal-input-line">
              <span className="prompt">longnt@portfolio:~$ </span>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="terminal-input"
                autoComplete="off"
                spellCheck="false"
              />
              <span
                className="cursor-blink"
                style={{
                  animation: "blink 1s infinite",
                  color: "#00ff00",
                  marginLeft: "2px",
                }}
              >
                █
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Terminal;
